import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { changeMyPassword, logout } from '../services/api';
import { BRAND_NAME } from '../branding';
import { passwordPolicyError } from '../utils/passwordPolicy';
import PasswordChecklist from '../components/PasswordChecklist';
import { showSuccess } from '../components/ToastNotifications';
import './Login.css';

// Forced password change (operator rule 2026-10-12): App.js renders ONLY this
// page while the signed-in user's mustChangePassword flag is set — the server
// also refuses every other API call until the new password is saved.
function ChangePasswordRequired({ user, setUser }) {
  const [currentPw, setCurrentPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [newPw2, setNewPw2] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    const policyError = passwordPolicyError(newPw, { username: user?.identity?.login_name || user?.username });
    if (policyError) return setError(policyError);
    if (newPw !== newPw2) return setError('The two new passwords do not match');

    setBusy(true);
    try {
      await changeMyPassword(currentPw, newPw);
      showSuccess('Password updated');
      setUser({ ...user, mustChangePassword: false });
      navigate('/dashboard');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to change password. Please try again.');
      setBusy(false);
    }
  };

  const handleSignOut = async () => {
    try {
      await logout();
    } catch (err) {
      // best-effort: the session may already be gone
    }
    setUser(null);
  };

  return (
    <div className="login-container">
      <div className="login-box">
        <div className="login-brand">{BRAND_NAME}</div>
        <h1 className="login-title">Set a new password</h1>
        <p className="login-subtitle">
          For everyone's security, every CRM account must set a new password before continuing. Choose one only you know.
        </p>

        {error && (
          <div className="login-error" role="alert">{error}</div>
        )}

        <form onSubmit={handleSubmit} className="login-form" autoComplete="on">
          <div className="form-group">
            <label htmlFor="currentPassword" className="form-label">Current password</label>
            <input
              type="password"
              id="currentPassword"
              className="form-control"
              value={currentPw}
              onChange={(e) => setCurrentPw(e.target.value)}
              required
              autoFocus
              autoComplete="current-password"
            />
          </div>

          <div className="form-group">
            <label htmlFor="newPassword" className="form-label">New password</label>
            <input
              type="password"
              id="newPassword"
              className="form-control"
              value={newPw}
              onChange={(e) => setNewPw(e.target.value)}
              required
              autoComplete="new-password"
            />
          </div>

          <div className="form-group">
            <label htmlFor="newPassword2" className="form-label">Repeat new password</label>
            <input
              type="password"
              id="newPassword2"
              className="form-control"
              value={newPw2}
              onChange={(e) => setNewPw2(e.target.value)}
              required
              autoComplete="new-password"
            />
          </div>

          <PasswordChecklist password={newPw} />

          <button
            type="submit"
            className="btn btn-primary btn-block"
            disabled={busy}
          >
            {busy ? 'Saving…' : 'Save new password'}
          </button>
        </form>

        <div style={{ textAlign: 'center', marginTop: 'var(--spacing-lg, 16px)' }}>
          <button
            type="button"
            className="btn btn-outline btn-small"
            onClick={handleSignOut}
            disabled={busy}
          >
            Sign out
          </button>
        </div>
      </div>
    </div>
  );
}

export default ChangePasswordRequired;
